<template>
  <page v-bind="{ loading }" :title="Naming.ClientSites" icon="city" :loading-text="`Loading ${Naming.Sites.toLowerCase()}…`">
    <div slot="tools">
      <form>
          <div class="buttons has-addons" slot="right">
            <router-link
              :to="{ name: 'site-admin-create' }"
              class="button is-rounded is-primary" left-icon="plus"
              v-if="hasAbility('Billow\\Models\\Site', 'create-sites')"
            >
              <icon icon="plus"/>
              <span>{{ Naming.Site }}</span>
            </router-link>
          </div>
      </form>
    </div>
    <div class="box is-paddingless">
      <div class="tabs is-toggle is-toggle-rounded is-raised is-rounded mt-1 is-inline-flex ml-1">
        <ul>
          <li :class="{ 'is-active pointer-events-none': filters.activeStatus === ''}">
            <a @click="filterBy('activeStatus')">Show All</a>
          </li>
          <li :class="{ 'is-active pointer-events-none': filters.activeStatus === 'active'}">
            <a @click="filterBy('activeStatus', 'active')">Active {{ Naming.Sites }}</a>
          </li>
          <li :class="{ 'is-active pointer-events-none': filters.activeStatus === 'deactivated'}">
            <a @click="filterBy('activeStatus', 'deactivated')">Deactivated {{ Naming.Sites }}</a>
          </li>
        </ul>
      </div>
      <div class="is-flex is-align-items-center w-full">
        <div class="flex-grow mx-2">
          <text-input v-model="filters.search"
                      @input="runFilters"
                      :placeholder="`Search ${Naming.Sites.toLowerCase()}`"
                      classes="level-item is-rounded">
            <div slot="right">
              <action-button @click="searchSites" left-icon="search" class="is-rounded">Search</action-button>
            </div>
          </text-input>
        </div>
        <div v-if="filters.activeStatus === 'deactivated' && $root.hasAbility('delete-sites', 'Billow\\Models\\Site')" class="mx-2">
          <action-button @click="setAllSites('activate')" class="is-rounded mr-1 is-success" :disabled="sites.data.length === 0" left-icon="play">Activate All</action-button>
          <action-button @click="setSelectedSites('activate')" class="is-rounded is-success" :disabled="sites.data.length === 0" left-icon="play">Activate Selected {{ Naming.Sites }}</action-button>
        </div>
        <div v-if="filters.activeStatus === 'active' && $root.hasAbility('delete-sites', 'Billow\\Models\\Site')" class="mx-2">
          <action-button @click="setAllSites('deactivate')" class="is-rounded mr-1 is-danger" :disabled="sites.data.length === 0" left-icon="stop">Deactivate All</action-button>
          <action-button @click="setSelectedSites('deactivate')" class="is-rounded is-danger" :disabled="sites.data.length === 0" left-icon="stop">Deactivate Selected {{ Naming.Sites }}</action-button>
        </div>
      </div>
      <table class="table is-fullwidth is-vcentered is-hoverable">
        <thead class="is-sticky">
        <tr>
          <th class="has-text-centered">{{ Naming.Site }}</th>
          <th class="has-text-centered">{{ Naming.Zones }}</th>
          <th class="has-text-centered">{{ Naming.Assets }}</th>
          <th class="has-text-centered">Open {{ Naming.Jobs }}</th>
          <th class="has-text-centered">{{ Naming.CustomFields }}</th>
          <th class="has-text-centered">Current Risk</th>
        </tr>
        </thead>
        <tbody>
        <tr class="cursor-pointer" @click="goTo(site)" v-for="site in sites.data" :key="site.id">
          <td class="is-flex is-align-items-center">
            <span @click.stop class="p-1 is-flex is-align-items-center" v-if="$root.hasAbility('delete-sites', 'Billow\\Models\\Site')">
              <checkbox-input
                v-if="filters.activeStatus === 'active'"
                :value="siteSelection(site.id, sitesToDeactivate)"
                @input="toggleSite(site.id, sitesToDeactivate)"
                classes="is-circle is-danger"
                :label="false"/>
              <checkbox-input
                v-if="filters.activeStatus === 'deactivated'"
                :value="siteSelection(site.id, sitesToActivate)"
                @input="toggleSite(site.id, sitesToActivate)"
                classes="is-circle is-success"
                :label="false"/>
            </span>
            <span class="flex-link">
              <avatar class="mr-1" :path="site.logo_url"/>
              <span :class="{ 'has-text-grey': site.deactivated_at }">{{ `${site.client.legal_name} - ${site.name}` }}</span>
            </span>
            <span v-if="site.deactivated_at" class="tag is-danger ml-1">Deactivated</span>
          </td>
          <td class="counter has-text-centered is-highlighted has-left-border">{{ site.zones_count }}</td>
          <td class="counter has-text-centered is-highlighted">{{ site.assets_count }}</td>
          <td class="counter has-text-centered is-highlighted has-right-border">{{ site.open_jobs_count }}</td>
          <td class="counter has-text-centered is-highlighted has-right-border">{{ site.custom_fields_count }}</td>
          <td class="has-text-centered">
            <current-risk :risk="site.risk"/>
          </td>
        </tr>
        </tbody>
      </table>

      <p class="has-text-centered" v-if="!sites.data.length">
        No {{ Naming.Sites.toLowerCase() }} found
      </p>

      <pager class="pl" :pageable="sites" @nav="goToPage"/>
    </div>
  </page>
</template>

<script>
import {mapGetters, mapState} from 'vuex'
import { debounce } from 'lodash'

export default {

  data: () => ({
    loading: true,
    going_to_page: false,
    namespace: 'site',
    filters: {
      search: '',
      activeStatus: ''
    },
  }),

  async created() {
    await this.loadSites()
    this.loading = false
  },

  computed: {
    ...mapState('site', ['sitesToActivate', 'sitesToDeactivate']),
    ...mapGetters('site', [
      'sites'
    ]),
    ...mapGetters('auth', [
      'user'
    ])
  },

  methods: {
    goTo(site) {
      this.$router.push({
        name: 'site-overview',
        params: { site: site.id }
      })
    },
    goToPage(path) {
      this.$router.push({
        path: path,
        query: this.filters
      })
      this.$scrollToTop()
    },
    loadSites() {
      this.$scrollToTop()
      return this.$store.dispatch('site/loadSites', {
        path: '/api' + this.$route.fullPath,
        params: this.$route.query
      })
    },
    searchSites() {
      this.$router.push({
        name: 'site-admin-index',
        query: this.filters
      })
    },
    hasAbility(entityType, name) {
      return this.user.abilities.filter(ability => ability.entity_type === entityType && ability.name === name).length > 0
    },
    filterBy(key, value = '') {
      this.filters[key] = value
      this.runFilter()
    },
    runFilter() {
      this.$router.push({
        name: this.$route.name,
        query: this.filters
      })
    },
    runFilters: debounce(function () {
      if (this.loading || this.going_to_page) return
      this.searchSites()
    }, 500),
    toggleSite(siteId, siteList) {
      const index = siteList.indexOf(siteId);
      if (index === -1) {
        siteList.push(siteId);
      } else {
        siteList.splice(index, 1);
      }
    },
    siteSelection(siteId, siteList) {
      return siteList.includes(siteId)
    },
    async setAllSites(status) {
      if (status === 'deactivate') {
        const { title, message, confirmText } = this.$lang.site.confirmMassDeactivation;
        const convertedTitle = await this.Convert(title);
        const convertedMessage = await this.Convert(message);
        const convertedConfirmText = await this.Convert(confirmText);

        if (await this.$confirm({
          title: convertedTitle,
          message: convertedMessage,
          confirmText: convertedConfirmText,
          confirmClass: 'is-danger'
        })) {
          const payload = {
            active_status: 'deactivate',
            deactivate_all: true
          }
          const response = await this.$store.dispatch('site/setSelectedSitesActiveStatus', payload)
          if (response.status === 202) {
            this.$toast.success('Success', `${this.Convert(response.data.message)}`)
          } else {
            this.$toast.success('Success', `All ${this.Naming.Sites} with no open ${this.Naming.Jobs} Deactivated`)
          }
        }
      } else if (status === 'activate') {
        const payload = {
          active_status: 'activate',
          activate_all: true
        }
        const response = await this.$store.dispatch('site/setSelectedSitesActiveStatus', payload)
        if (response.status === 202) {
          this.$toast.success('Success', `${this.Convert(response.data.message)}`)
        } else {
          this.$toast.success('Success', `All ${this.Naming.Sites} Activated`)
        }
      }
      await this.loadSites()
    },
    async setSelectedSites(status) {
      this.loading = true
      try {
        if (status === 'deactivate') {
          const { title, message, confirmText } = this.$lang.site.confirmMassDeactivation;
          const convertedTitle = await this.Convert(title);
          const convertedMessage = await this.Convert(message);
          const convertedConfirmText = await this.Convert(confirmText);

          if (await this.$confirm({
            title: convertedTitle,
            message: convertedMessage,
            confirmText: convertedConfirmText,
            confirmClass: 'is-danger'
          })) {
            const payload = {
              active_status: 'deactivate',
              site_ids: this.sitesToDeactivate
            }
            const response = await this.$store.dispatch('site/setSelectedSitesActiveStatus', payload)
            let message = response.data.skipped
                ? `${response.data.deactivated} ${this.Naming.Sites} Deactivated and ${response.data.skipped} ${this.Naming.Sites} have open ${this.Naming.Jobs}`
                : `${response.data.deactivated} ${this.Naming.Sites} Deactivated`
            if (response.status === 202) {
              message = this.$toast.success(`${this.Convert(response.data.message)}`)
            }
            this.$toast.success('Success', message)
          }
        } else if (status === 'activate') {
          const payload = {
            active_status: 'activate',
            site_ids: this.sitesToActivate
          }
          const response = await this.$store.dispatch('site/setSelectedSitesActiveStatus', payload)
          if (response.status === 202) {
            this.$toast.success('Success', `${this.Convert(response.data.message)}`)
          } else {
            this.$toast.success('Success', `${response.data.activated} ${this.Naming.Sites} Activated`)
          }
        }
      } catch (error) {
        this.$whoops()
      } finally {
        await this.loadSites()
        this.loading = false
      }
    }
  },

  watch: {
    '$route': 'loadSites'
  }
}
</script>
